<template>
    <form class="header__search-form" action="/search" role="search" @submit.prevent="submit">
        <div class="input-group mb-0">
            <button class="input-group-text search-form__icon" type="submit" aria-label="جستجو">
                <span class="icon-Search"></span>
            </button>
            <input
                ref="input"
                v-model="query"
                name="search"
                type="search"
                autocomplete="off"
                class="form-control search-form__text-input"
                :placeholder="`جستجو در ${storeTitle}`"
                aria-label="جستجو در فروشگاه"
                :aria-expanded="isOpen"
                aria-controls="header-search-suggestions"
                @focus="openIfResults"
                @input="queueSearch"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.enter.prevent="selectActiveOrSubmit"
                @keydown.esc.prevent="close"
                @blur="closeWithDelay"
            >
        </div>

        <div v-if="isOpen" id="header-search-suggestions" class="header-search-suggestions" role="listbox">
            <div v-if="loading" class="header-search-suggestions__state">در حال جستجو…</div>
            <template v-else>
                <section v-if="results.products.length" class="header-search-suggestions__section">
                    <p class="header-search-suggestions__title">کالاها</p>
                    <a
                        v-for="(product, index) in results.products"
                        :key="`product-${product.id}`"
                        :href="product.url"
                        class="header-search-suggestions__product"
                        :class="{ 'is-active': activeIndex === index }"
                        role="option"
                        @mouseenter="activeIndex = index"
                    >
                        <span class="header-search-suggestions__image">
                            <img v-if="product.image" :src="product.image" :alt="product.title" width="48" height="48">
                            <span v-else class="icon-Image" aria-hidden="true"></span>
                        </span>
                        <span class="header-search-suggestions__product-copy">
                            <span class="header-search-suggestions__product-title">{{ product.title }}</span>
                            <span v-if="product.second_title" class="header-search-suggestions__product-subtitle">{{ product.second_title }}</span>
                        </span>
                        <span class="header-search-suggestions__price" :class="{ 'is-unavailable': !product.available }">
                            <template v-if="!product.available">ناموجود</template>
                            <template v-else-if="product.price">{{ price(product.price) }} تومان</template>
                            <template v-else>استعلام قیمت</template>
                        </span>
                    </a>
                </section>

                <section v-if="results.brands.length || results.categories.length" class="header-search-suggestions__section header-search-suggestions__section--links">
                    <p class="header-search-suggestions__title">دسته‌بندی و برند</p>
                    <a
                        v-for="(item, index) in quickLinks"
                        :key="`${item.type}-${item.id}`"
                        :href="item.url"
                        class="header-search-suggestions__link"
                        :class="{ 'is-active': activeIndex === results.products.length + index }"
                        role="option"
                        @mouseenter="activeIndex = results.products.length + index"
                    >
                        <span>{{ item.type === 'brand' ? 'برند' : 'دسته‌بندی' }}</span>
                        <strong>{{ item.title }}</strong>
                    </a>
                </section>

                <p v-if="!hasResults" class="header-search-suggestions__state">نتیجه‌ای برای «{{ query }}» پیدا نشد.</p>
                <a class="header-search-suggestions__all" :href="searchUrl">مشاهده همه نتایج برای «{{ query }}»</a>
            </template>
        </div>
    </form>
</template>

<script>
export default {
    props: {
        initialQuery: {type: String, default: ''},
        storeTitle: {type: String, required: true},
    },
    data() {
        return {
            query: this.initialQuery,
            results: {products: [], brands: [], categories: []},
            loading: false,
            isOpen: false,
            activeIndex: -1,
            debounceTimer: null,
            closeTimer: null,
            abortController: null,
            requestId: 0,
            cache: new Map(),
        };
    },
    computed: {
        hasResults() {
            return this.results.products.length || this.results.brands.length || this.results.categories.length;
        },
        quickLinks() {
            return [
                ...this.results.categories.map(item => ({...item, type: 'category'})),
                ...this.results.brands.map(item => ({...item, type: 'brand'})),
            ];
        },
        selectableItems() {
            return [...this.results.products, ...this.quickLinks];
        },
        searchUrl() {
            return `/search?search=${encodeURIComponent(this.query.trim())}`;
        },
    },
    beforeUnmount() {
        clearTimeout(this.debounceTimer);
        clearTimeout(this.closeTimer);
        this.abortController?.abort();
    },
    methods: {
        queueSearch() {
            clearTimeout(this.debounceTimer);
            this.activeIndex = -1;
            const term = this.query.trim();

            if (term.length < 2) {
                this.results = {products: [], brands: [], categories: []};
                this.loading = false;
                this.isOpen = false;
                return;
            }

            this.isOpen = true;
            this.debounceTimer = setTimeout(() => this.fetchSuggestions(term), 220);
        },
        async fetchSuggestions(term) {
            const cached = this.cache.get(term);
            if (cached && Date.now() - cached.createdAt < 60000) {
                this.results = cached.data;
                this.loading = false;
                return;
            }

            this.abortController?.abort();
            this.abortController = new AbortController();
            const requestId = ++this.requestId;
            this.loading = true;

            try {
                const response = await this.axios.get('/api/v1/search', {
                    params: {key: term},
                    signal: this.abortController.signal,
                });

                if (requestId !== this.requestId) return;
                this.results = response.data;
                this.cache.set(term, {data: response.data, createdAt: Date.now()});
                if (this.cache.size > 25) this.cache.delete(this.cache.keys().next().value);
            } catch (error) {
                if (error.code !== 'ERR_CANCELED' && requestId === this.requestId) {
                    this.results = {products: [], brands: [], categories: []};
                }
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        openIfResults() {
            clearTimeout(this.closeTimer);
            if (this.query.trim().length >= 2) {
                this.isOpen = true;
                if (!this.hasResults && !this.loading) this.queueSearch();
            }
        },
        closeWithDelay() {
            this.closeTimer = setTimeout(() => this.close(), 160);
        },
        close() {
            clearTimeout(this.closeTimer);
            this.isOpen = false;
            this.activeIndex = -1;
        },
        moveActive(direction) {
            if (!this.isOpen) {
                this.openIfResults();
                return;
            }
            const length = this.selectableItems.length;
            if (!length) return;
            this.activeIndex = (this.activeIndex + direction + length) % length;
        },
        selectActiveOrSubmit() {
            const item = this.selectableItems[this.activeIndex];
            if (item) {
                window.location.href = item.url;
                return;
            }
            this.submit();
        },
        submit() {
            if (this.query.trim()) window.location.href = this.searchUrl;
        },
        price(value) {
            return new Intl.NumberFormat('fa-IR').format(value);
        },
    },
};
</script>
